export const LANDING_FAQ_PATH = '/preguntas-frecuentes'

export interface LandingFaqItem {
  id: string
  question: string
  answer: string
}

export interface LandingFaqSection {
  id: string
  title: string
  items: LandingFaqItem[]
}

export const LANDING_FAQ_SECTIONS: LandingFaqSection[] = [
  {
    id: 'getting-started',
    title: 'Empezar',
    items: [
      {
        id: 'is-free',
        question: '¿Escribonauta es gratis?',
        answer:
          'Sí. Creemos en la creatividad ilimitada: Tripulante es gratuito y te permite sacar tu historia adelante de inicio a fin, con proyectos y fichas sin límite. Capitán, nuestro plan premium (próximamente), es para quien quiera mejorar aún más la experiencia.',
      },
      {
        id: 'try-without-account',
        question: '¿Puedo probar la app sin registrarme?',
        answer:
          'Sí. Puedes comenzar como invitado desde el inicio. La exportación a Word y la sincronización entre dispositivos requieren una cuenta registrada.',
      },
      {
        id: 'project-types',
        question: '¿Puedo tener varios tipos de proyecto?',
        answer:
          'Sí. Puedes crear novelas, antologías de cuentos, colecciones de poesía y guiones; cada uno con su propia organización.',
      },
    ],
  },
  {
    id: 'limits',
    title: 'Límites de tu biblioteca',
    items: [
      {
        id: 'projects-limit',
        question: '¿Cuántos proyectos puedo tener en Escribonauta?',
        answer: 'No existe un límite. Puedes escribir todo lo que quieras.',
      },
      {
        id: 'character-sheets-limit',
        question: '¿Cuántas fichas de personaje puedo tener en Escribonauta?',
        answer:
          'No hay un límite. Puedes crear todas las fichas que necesites para organizar tu historia.',
      },
      {
        id: 'words-chapters-limit',
        question: '¿Hay límite de palabras, capítulos o partes?',
        answer:
          'No imponemos un tope para escribir. Organiza tu obra con tantos capítulos y partes como necesites.',
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacidad de tu manuscrito',
    items: [
      {
        id: 'ai-policy',
        question: '¿Cuál es la política de IA?',
        answer:
          'Ningún modelo de IA es entrenado con tus historias ni tiene acceso a tu manuscrito para leerlo o analizarlo. Tu borrador es privado: solo tú lo editas, salvo lo que decidas compartir de forma explícita (por ejemplo, una versión para lectores beta).',
      },
      {
        id: 'who-sees-projects',
        question: '¿Quién puede ver mis proyectos además de mí?',
        answer:
          'Por defecto, nadie más. Otros solo ven lo que tú envíes a propósito: un enlace de lectores beta, un snapshot o lo que publiques en la comunidad con tu alias.',
      },
    ],
  },
  {
    id: 'plans',
    title: 'Planes',
    items: [
      {
        id: 'free-vs-paid',
        question: '¿Qué incluye el plan gratuito frente al de pago?',
        answer:
          'Con Tripulante puedes escribir y terminar tu obra de punta a punta: edición, planificación, proyectos y fichas sin límite. No necesitas un plan de pago para sacar tu historia adelante. Capitán (próximamente) es opcional y suma herramientas para mejorar tu experiencia: más análisis del texto, exportación y maquetación, versiones con comparador, planificación ampliada, más pizarras y más seguimiento de la escritura.',
      },
    ],
  },
  {
    id: 'beta-readers',
    title: 'Lectores beta',
    items: [
      {
        id: 'what-are-beta-readers',
        question: '¿Qué son los lectores beta y cómo funcionan?',
        answer:
          'Generas una versión congelada de tu manuscrito, compartes el enlace y los lectores registrados leen capítulo a capítulo y dejan comentarios. Tú revisas el feedback desde el detalle de esa versión.',
      },
      {
        id: 'beta-readers-limit',
        question: '¿Cuántos lectores beta puedo invitar?',
        answer:
          'Puedes limitar cuántas personas aceptan el enlace y desactivarlo cuando quieras. Así controlas quién entra y cuándo deja de estar disponible.',
      },
    ],
  },
  {
    id: 'data',
    title: 'Exportar y cuenta',
    items: [
      {
        id: 'export',
        question: '¿Puedo exportar mi obra?',
        answer:
          'Los usuarios registrados pueden exportar proyectos a Word. Formatos y opciones de maquetación adicionales formarán parte del plan Capitán cuando esté disponible.',
      },
      {
        id: 'account-deletion',
        question: '¿Qué pasa con mis proyectos si elimino la cuenta?',
        answer:
          'Al solicitar el cierre desde tu perfil, la cuenta queda pendiente de eliminación durante 14 días; puedes restaurarla iniciando sesión en ese período. Pasado el plazo, los datos se eliminan de forma permanente según la Política de Privacidad.',
      },
    ],
  },
  {
    id: 'devices',
    title: 'Dispositivos y conexión',
    items: [
      {
        id: 'cross-device',
        question: '¿Puedo escribir desde el celular y seguir en el computador?',
        answer:
          'Sí, con cuenta registrada: tu trabajo se sincroniza entre dispositivos mientras la sesión esté activa. También puedes probar la app móvil, que está en beta abierta.',
      },
      {
        id: 'offline',
        question: '¿Funciona sin conexión o solo en línea?',
        answer:
          'Necesitas internet para sincronizar y para la mayoría de funciones de comunidad. Si se corta la conexión mientras escribes, el editor intenta conservar cambios localmente y subirlos al volver en línea. En modo invitado, el trabajo depende del almacenamiento del navegador.',
      },
    ],
  },
  {
    id: 'community',
    title: 'Comunidad',
    items: [
      {
        id: 'community-visibility',
        question: '¿Lo que publico en la comunidad es visible para cualquiera?',
        answer:
          'La comunidad es para usuarios registrados con un alias público. Lo que compartas allí (perfil, grupos u otras publicaciones) lo ven otros participantes según cada espacio; no sustituye ni expone tu manuscrito privado.',
      },
    ],
  },
  {
    id: 'help',
    title: 'Ayuda',
    items: [
      {
        id: 'languages',
        question: '¿En qué idiomas está la interfaz?',
        answer: 'La interfaz está en español, pensada para escritores hispanohablantes.',
      },
      {
        id: 'support',
        question: '¿Cómo contacto soporte?',
        answer: 'Escríbenos a escribonauta.cl@gmail.com y te responderemos lo antes posible.',
      },
    ],
  },
]

export const LANDING_FAQ_ITEMS: LandingFaqItem[] = LANDING_FAQ_SECTIONS.flatMap(
  (section) => section.items,
)
